import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  LogIn, 
  UserPlus, 
  Mail, 
  Lock, 
  User,
  Users,
  Building2,
  Shield,
  CheckCircle,
  ArrowLeft,
  Loader2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [user, setUser] = useState<any>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar se já está logado
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        navigate('/');
      }
    };

    checkUser();

    // Listener para mudanças no estado de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          setUser(session.user);
          navigate('/');
        } else {
          setUser(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem",
        variant: "destructive"
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Erro", 
        description: "A senha deve ter pelo menos 6 caracteres",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName
          }
        }
      });

      if (error) {
        if (error.message.includes('User already registered')) {
          toast({
            title: "Conta já existe",
            description: "Este email já possui uma conta. Tente fazer login.",
            variant: "destructive"
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Conta criada!",
          description: "Verifique seu email para confirmar a conta",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao criar conta",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast({
            title: "Credenciais inválidas",
            description: "Email ou senha incorretos",
            variant: "destructive"
          });
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao fazer login",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignInWithGoogle = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Erro no Login com Google",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const Feature = ({ icon: Icon, title, description }: { icon: typeof Users; title: string; description: string }) => (
    <div className="flex items-center gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-background/70">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="min-w-0">
        <h3 className="font-semibold text-foreground">{title}</h3>
        <p className="mt-0.5 text-sm leading-5 text-muted-foreground">{description}</p>
      </div>
    </div>
  );

  const Field = ({ id, label, type, placeholder, value, onChange, icon: Icon, autoComplete }: {
    id: string; label: string; type: string; placeholder: string; value: string;
    onChange: (value: string) => void; icon: typeof Mail; autoComplete: string;
  }) => (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold">{label}</Label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input id={id} type={type} placeholder={placeholder} value={value}
          onChange={(event) => onChange(event.target.value)} autoComplete={autoComplete}
          className="h-12 rounded-md border-input bg-background pl-10 text-base shadow-sm" required />
      </div>
    </div>
  );

  return (
    <main className="flex min-h-[100svh] w-full bg-background">
      <section className="relative hidden w-[44%] min-w-[430px] overflow-hidden border-r border-border bg-primary/10 lg:flex">
        <div className="flex min-h-full w-full flex-col items-center justify-center px-10 py-8 xl:px-12">
          <div className="relative flex w-full max-w-[470px] items-center justify-center py-6">
            <div className="absolute inset-x-8 top-1/2 h-36 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
            <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" aria-hidden="true" />
            <img
              src="/logoasad.png"
              alt="Allana Sthéfany Advocacia"
              className="relative h-auto w-full max-w-[440px] object-contain drop-shadow-[0_12px_18px_hsl(var(--primary)/0.16)]"
            />
          </div>
          <div className="mt-2 w-full max-w-md border-t border-primary/20 pt-6">
            <h2 className="mb-6 text-center text-xl font-bold text-foreground">Sistema jurídico completo</h2>
            <div className="grid gap-5">
              <Feature icon={Users} title="Gestão de clientes" description="Informações e históricos organizados em um só lugar." />
              <Feature icon={Building2} title="Controle de processos" description="Prazos e atividades acompanhados com clareza." />
              <Feature icon={Shield} title="Segurança e privacidade" description="Dados protegidos em conformidade com a LGPD." />
              <Feature icon={CheckCircle} title="Agenda inteligente" description="Compromissos e lembretes para toda a rotina." />
            </div>
          </div>
        </div>
      </section>

      <section className="relative flex min-h-[100svh] flex-1 items-center justify-center overflow-y-auto px-5 py-8 sm:px-8 lg:px-12">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="absolute right-4 top-4 text-muted-foreground sm:right-6 sm:top-6">
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Voltar ao início</span>
          <span className="sm:hidden">Voltar</span>
        </Button>

        <div className="w-full max-w-[430px] py-12 lg:py-6">
          <div className="relative mb-6 flex justify-center overflow-hidden py-3 lg:hidden">
            <div className="absolute inset-x-10 top-1/2 h-20 -translate-y-1/2 rounded-full bg-primary/10 blur-2xl" aria-hidden="true" />
            <div className="absolute inset-x-4 top-1/2 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent" aria-hidden="true" />
            <img
              src="/logoasad.png"
              alt="Allana Sthéfany Advocacia"
              className="relative h-auto w-full max-w-[300px] object-contain drop-shadow-[0_8px_12px_hsl(var(--primary)/0.14)] sm:max-w-[340px]"
            />
          </div>

          <Tabs value={isLogin ? "login" : "signup"} onValueChange={(value) => setIsLogin(value === "login")}>
            <TabsList className="mb-7 grid h-12 w-full grid-cols-2 rounded-md bg-muted p-1">
              <TabsTrigger value="login" className="flex items-center gap-2">
                <LogIn className="w-4 h-4" />
                Entrar
              </TabsTrigger>
              <TabsTrigger value="signup" className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Cadastrar
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-0">
              <div className="mb-7 text-center">
                <h1 className="text-3xl font-bold text-foreground">Bem-vindo de volta</h1>
                <p className="mt-2 text-sm text-muted-foreground">Acesse sua conta para continuar</p>
              </div>
              <form onSubmit={handleSignIn} className="space-y-5">
                    <Field id="login-email" label="E-mail" type="email" placeholder="seu@email.com" value={email} onChange={setEmail} icon={Mail} autoComplete="email" />
                    <Field id="login-password" label="Senha" type="password" placeholder="Sua senha" value={password} onChange={setPassword} icon={Lock} autoComplete="current-password" />

                    <Button type="submit" className="h-12 w-full text-base font-semibold" disabled={loading}>
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {loading ? "Entrando..." : "Entrar"}
                    </Button>

                    <div className="relative py-1">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                      <div className="relative flex justify-center text-[11px] font-medium uppercase"><span className="bg-background px-3 text-muted-foreground">Ou continue com</span></div>
                    </div>

                    <Button type="button" variant="outline" className="h-12 w-full text-base font-semibold" onClick={handleSignInWithGoogle} disabled={loading}>
                      <svg role="img" viewBox="0 0 24 24" className="mr-2 h-4 w-4"><path fill="currentColor" d="M12.48 10.92v3.28h7.84c-.24 1.84-.85 3.18-1.73 4.1-1.02 1.02-2.3 1.84-4.32 1.84-3.6 0-6.5-2.95-6.5-6.5s2.9-6.5 6.5-6.5c1.95 0 3.35.73 4.13 1.5l2.44-2.44C18.4 2.84 15.78 2 12.48 2c-5.45 0-9.94 4.45-9.94 9.94s4.49 9.94 9.94 9.94c5.22 0 9.4-3.5 9.4-9.56 0-.6-.05-1.18-.15-1.72H12.48z"></path></svg>
                      Entrar com Google
                    </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-0">
              <div className="mb-6 text-center">
                <h1 className="text-3xl font-bold text-foreground">Criar sua conta</h1>
                <p className="mt-2 text-sm text-muted-foreground">Preencha seus dados para solicitar acesso</p>
              </div>
              <form onSubmit={handleSignUp} className="space-y-4">
                    <Field id="signup-name" label="Nome completo" type="text" placeholder="Seu nome completo" value={fullName} onChange={setFullName} icon={User} autoComplete="name" />
                    <Field id="signup-email" label="E-mail" type="email" placeholder="seu@email.com" value={email} onChange={setEmail} icon={Mail} autoComplete="email" />
                    <Field id="signup-password" label="Senha" type="password" placeholder="Mínimo de 6 caracteres" value={password} onChange={setPassword} icon={Lock} autoComplete="new-password" />
                    <Field id="signup-confirm-password" label="Confirmar senha" type="password" placeholder="Digite a senha novamente" value={confirmPassword} onChange={setConfirmPassword} icon={Lock} autoComplete="new-password" />

                    <Button type="submit" className="h-12 w-full text-base font-semibold" disabled={loading}>
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {loading ? "Criando conta..." : "Criar conta"}
                    </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="mt-7 text-center text-xs leading-5 text-muted-foreground">Ao continuar, você aceita nossos termos de uso e política de privacidade.</p>
        </div>
      </section>
    </main>
  );
};

export default Auth;